// Using it as a body for a referee payout modal window

import { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useModal } from './ModalProvider';
import { useFirebase } from './FirebaseContextProvider';
import { payoutPay } from '../utils/firebase';
import { PayoutStatuses } from '../utils/enums';
import { moneyStr } from '../utils/gameReport';
import InputMoney from './InputMoney';
import Spinner from './Spinner';
import './RefereePayModalBody.scss';

// 32 hex characters, within the ^[A-Za-z0-9_-]{8,64}$ the backend accepts.
// randomUUID is missing outside a secure context — a build opened over a LAN
// address — and the modal must not die on the way to a payout
const newIdempotencyKey = () => {
  if (crypto.randomUUID) return crypto.randomUUID().replace(/-/g, '');

  return Array.from(crypto.getRandomValues(new Uint8Array(16)), (byte) =>
    byte.toString(16).padStart(2, '0'),
  ).join('');
};

export default function RefereePayModalBody({
  tournamentId,
  refereeId,
  refereeName,
  amount,
}) {
  const [spinner, setSpinner] = useState(false);
  // A key that has gone out owns the amount: the backend answers a repeat from
  // what it stored, so an edited field would price a payout nobody can send
  const [keyIsSent, setKeyIsSent] = useState(false);
  const { closeModal } = useModal();
  const { tournamentsById } = useFirebase();
  // Every attempt at the same payout carries it, so a lost answer can be
  // retried without paying twice. Minted per mount, and the caller remounts the
  // body on every open: reopening the modal is the admin asking to pay again
  const idempotencyKey = useRef(null);

  if (!idempotencyKey.current) idempotencyKey.current = newIdempotencyKey();
  // A state flag would not do: two Enter presses in one tick both read the
  // state of the render they came from, and each would send its own request
  const isSending = useRef(false);
  // A modal closed by hand mid-flight leaves the request running. Whatever it
  // answers must not close whatever the admin opened next
  const isMounted = useRef(true);

  // Re-armed in the body, not just initialized: StrictMode runs the cleanup
  // between its two mount passes, and a flag left down there would swallow the
  // close of every payout that follows
  useEffect(() => {
    isMounted.current = true;

    return () => (isMounted.current = false);
  }, []);

  const tournamentName = tournamentsById.get(tournamentId)?.name;

  // The report's total is only a suggestion: the admin may top up, cut back or
  // pay for half a tournament, and whatever is sent is what gets paid
  const initialValues = {
    amount: amount.toFixed(2),
  };

  const validationSchema = new Yup.ObjectSchema({
    amount: Yup.number()
      .typeError('Must be a number')
      .positive('Must be positive')
      .required('Required'),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      if (isSending.current) return;
      isSending.current = true;
      setSpinner(true);
      setKeyIsSent(true);
      try {
        const res = await payoutPay({
          tournamentId,
          items: [{ refereeId, amount: +values.amount }],
          idempotencyKey: idempotencyKey.current,
        });
        // A payout that never left is not an error: the call succeeds and the
        // refusal arrives as the status of the line, with the reason to fix
        // The amount comes back rather than off the form: a repeat is answered
        // from what was stored under the key, and that is the sum that went out
        const { status, reason, amount: sent } = res.data.items[0];

        if (status === PayoutStatuses.FAILED) {
          // The money did not move and the backend has recorded that under the
          // key, so a retry needs a new one to be a payout rather than an echo
          idempotencyKey.current = newIdempotencyKey();
          if (isMounted.current) setKeyIsSent(false);
          toast.error(reason ?? `Failed to pay ${refereeName}`);
        } else if (status === PayoutStatuses.UNKNOWN) {
          toast.warn(
            'PayPal did not answer. The payout is recorded — press Pay again to check on it',
          );
        } else {
          toast.success(`${moneyStr(sent)} sent to ${refereeName}`);
          if (isMounted.current) closeModal();
        }
      } catch (error) {
        // No answer is not a failed payout: the money may well be out. The key
        // stays put, so pressing Pay again asks about this payout rather than
        // starting a second one — unless the arguments were refused, which the
        // backend does before writing anything, leaving nothing to ask about
        if (error?.code === 'functions/invalid-argument') {
          idempotencyKey.current = newIdempotencyKey();
          if (isMounted.current) setKeyIsSent(false);
        }
        toast.error(error?.message ?? 'Failed to send the payout');
      } finally {
        isSending.current = false;
        if (isMounted.current) setSpinner(false);
      }
    },
  });

  return (
    <form className='referee-pay-modal-body' onSubmit={formik.handleSubmit}>
      <p className='font-16'>
        {'Pay '}
        <span className='font-medium'>{refereeName}</span>
        {' for refereeing the '}
        <span className='font-medium'>{tournamentName}</span>
        {' tournament'}
      </p>
      <p className='font-16'>
        The money is sent to the Venmo account of their phone number
      </p>
      <div className='amount-pay-wrap'>
        <InputMoney
          name='amount'
          value={formik.values.amount}
          onChange={(value) => formik.setFieldValue('amount', value)}
          onBlur={formik.handleBlur}
          disabled={keyIsSent}
          error={
            formik.touched.amount && formik.errors.amount
              ? formik.errors.amount
              : null
          }
        />
        <button type='submit' disabled={spinner}>
          Pay
        </button>
      </div>
      <Spinner show={spinner} />
    </form>
  );
}
